import { format } from 'date-fns';

/**
 * Format a time with hours and minutes according to user preference (12h or 24h format)
 * 
 * @param hours - Hours component (0-23)
 * @param minutes - Minutes component (0-59)
 * @param timeFormat - Format preference ('12h' or '24h')
 * @returns Formatted time string
 */
export const formatTimeWithPreference = (
  hours: number, 
  minutes: number, 
  timeFormat: '12h' | '24h' = '12h'
): string => {
  if (timeFormat === '12h') {
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12; // Convert 0 to 12 for 12h format
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  } else {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }
};

/**
 * Format a date string safely for display, avoiding timezone issues with YYYY-MM-DD.
 * If the string is in YYYY-MM-DD, format as MM/DD/YYYY manually.
 * Otherwise, fallback to date-fns formatting.
 */
export const formatDate = (
  dateString: string,
  formatStr: string = 'MMMM d, yyyy'
): string => {
  // If already in MM/DD/YYYY, just return
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) return dateString;
  // If in YYYY-MM-DD, format manually
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split('-');
    return `${month}/${day}/${year}`;
  }
  // Fallback to date-fns
  try {
    return format(new Date(dateString), formatStr);
  } catch {
    return dateString;
  }
};

/**
 * Parse a time string in HH:MM format and return hours and minutes as numbers
 * 
 * @param timeString - Time string in format "HH:MM"
 * @returns Object with hours and minutes
 */
export const parseTimeString = (timeString: string): { hours: number; minutes: number } => {
  const [hours, minutes] = timeString.split(':').map(Number);
  return { hours: hours || 0, minutes: minutes || 0 };
}; 